/* global gsap, ScrollTrigger */

class ServicesStoryboard {
  constructor() {
    this.isScrollTriggered = false;
    this.currentScrollCategory = null;
    this.isMouseHovering = false;
    this.lastUpdateTime = 0;
    this.updateDebounceDelay = 150; // Minimum time between updates
    this.init();
  }

  init() {
    document.readyState === "loading"
      ? document.addEventListener("DOMContentLoaded", () => this.setup())
      : this.setup();
  }

  setup() {
    this.titleItems = document.querySelectorAll(
      ".services-storyboard__titles li[data-category]",
    );
    this.tagItems = document.querySelectorAll(
      ".services-storyboard__tag[data-category]",
    );
    this.description = document.querySelector(
      ".services-storyboard__description",
    );

    if (!this.titleItems.length || !this.tagItems.length || !this.description)
      return;

    this.setupDescriptions();
    this.initializeDescription();
    this.setupEvents();
    // this.setupScrollTriggers();

    // Delay mobile default state to ensure everything is ready
    setTimeout(() => {
      this.handleMobileDefaultState();
    }, 200);
  }

  setupDescriptions() {
    // Build descriptions object from data attributes
    this.descriptions = {};
    this.titleItems.forEach((item) => {
      const category = item.dataset.category;
      const description = item.dataset.description;
      if (category && description) {
        this.descriptions[category] = description;
      }
    });
  }

  initializeDescription() {
    // Set initial state - hidden with no content
    gsap.set(this.description, {
      opacity: 0,
      y: 10,
    });
    this.description.textContent = "";
  }

  handleMobileDefaultState() {
    // Activate first title by default on both mobile and desktop
    if (this.titleItems.length > 0) {
      // Only set default if no mouse hovering and no scroll trigger active
      if (!this.isMouseHovering && !this.isScrollTriggered) {
        this.restoreDefaultState();
      }
    }
  }

  restoreDefaultState() {
    // Activate the first title and show its description
    if (this.titleItems.length > 0) {
      const firstTitle = this.titleItems[0];
      const firstCategory = firstTitle.dataset.category;

      // Set as active and show description
      this.highlightCategory(firstCategory);

      // Force show description - animate from bottom
      const descriptionText = this.descriptions[firstCategory];
      if (descriptionText) {
        this.description.textContent = descriptionText;
        gsap.set(this.description, {
          y: 10,
          opacity: 0,
        });
        gsap.to(this.description, {
          opacity: 1,
          y: 0,
          duration: 0.4,
          ease: "power2.out",
        });
      }
    }
  }

  setupEvents() {
    // Setup events for title items
    this.titleItems.forEach((item) => {
      const category = item.dataset.category;

      item.addEventListener("mouseenter", () => {
        // Mouse hover always takes priority over scroll triggers
        this.isMouseHovering = true;
        this.highlightCategory(category);
        this.updateDescription(category);
      });

      item.addEventListener("mouseleave", () => {
        this.isMouseHovering = false;
        // Keep the last hovered item active - don't change anything
        // The item will remain active until another item is hovered
      });
    });

    // Setup events for tag items
    this.tagItems.forEach((tag) => {
      const category = tag.dataset.category;

      tag.addEventListener("mouseenter", () => {
        // Mouse hover always takes priority over scroll triggers
        this.isMouseHovering = true;
        this.highlightCategory(category);
        this.updateDescription(category);
      });

      tag.addEventListener("mouseleave", () => {
        this.isMouseHovering = false;
        // Keep the last hovered item active - don't change anything
        // The item will remain active until another item is hovered
      });
    });
  }

  highlightCategory(category) {
    // Add active class to title
    this.titleItems.forEach((item) => {
      const isActive = item.dataset.category === category;
      item.classList.toggle("is-active", isActive);
    });

    // Highlight matching tags and dim others
    this.tagItems.forEach((tag) => {
      if (tag.dataset.category === category) {
        tag.classList.add("is-highlighted");
        tag.classList.remove("is-dimmed");
      } else {
        tag.classList.add("is-dimmed");
        tag.classList.remove("is-highlighted");
      }
    });
  }

  clearHighlight() {
    // Remove active class from titles
    this.titleItems.forEach((item) => {
      item.classList.remove("is-active");
    });

    // Remove highlight and dim classes from tags
    this.tagItems.forEach((tag) => {
      tag.classList.remove("is-highlighted", "is-dimmed");
    });
  }

  updateDescription(category) {
    const now = performance.now();
    const timeSinceLastUpdate = now - this.lastUpdateTime;

    // Debounce rapid updates
    if (timeSinceLastUpdate < this.updateDebounceDelay) {
      return;
    }

    const newText = this.descriptions[category];
    if (newText && newText !== this.description.textContent) {
      this.lastUpdateTime = now;

      // Always animate from bottom (y: 10) to top (y: 0)
      if (this.description.textContent.trim() === "") {
        // No existing content, animate from bottom
        this.description.textContent = newText;
        gsap.set(this.description, { y: 10, opacity: 0 });
        gsap.to(this.description, {
          opacity: 1,
          y: 0,
          duration: 0.4,
          ease: "power2.out",
        });
      } else {
        // Animate text change - always fade out while moving to y: 10, then fade in from y: 10 to y: 0
        gsap.to(this.description, {
          opacity: 0,
          y: 10,
          duration: 0.15,
          ease: "power2.in",
          onComplete: () => {
            this.description.textContent = newText;
            // Ensure we start from y: 10 before animating up
            gsap.set(this.description, { y: 10, opacity: 0 });
            gsap.to(this.description, {
              opacity: 1,
              y: 0,
              duration: 0.4,
              ease: "power2.out",
            });
          },
        });
      }
    }
  }

  resetDescription() {
    // Hide the description paragraph when hover is released
    gsap.to(this.description, {
      opacity: 0,
      y: 10,
      duration: 0.2,
      onComplete: () => {
        this.description.textContent = "";
      },
    });
  }

  updateActiveScrollCategory() {
    // Find which scroll trigger is currently active
    const activeTriggers = ScrollTrigger.getAll().filter((trigger) => {
      return (
        trigger.trigger &&
        trigger.trigger.closest(".services-storyboard") &&
        trigger.isActive
      );
    });

    if (activeTriggers.length > 0) {
      // Get the first active trigger (closest to viewport)
      const activeTrigger = activeTriggers[0];
      const activeItem = activeTrigger.trigger;
      const activeCategory = activeItem.dataset.category;

      this.currentScrollCategory = activeCategory;

      // Only highlight if mouse is not hovering
      if (!this.isMouseHovering) {
        this.highlightCategory(activeCategory);
        this.updateDescription(activeCategory);
      }
    } else {
      // Check if we're near the bottom of the page and should keep the last item visible
      const scrollY = window.scrollY;
      const documentHeight = document.documentElement.scrollHeight;
      const viewportHeight = window.innerHeight;
      const isNearBottom = scrollY + viewportHeight >= documentHeight - 100; // Within 100px of bottom

      if (isNearBottom && this.titleItems.length > 0) {
        // Keep the last item visible when near bottom of page
        const lastItem = this.titleItems[this.titleItems.length - 1];
        const lastCategory = lastItem.dataset.category;

        this.isScrollTriggered = true;
        this.currentScrollCategory = lastCategory;

        if (!this.isMouseHovering) {
          this.highlightCategory(lastCategory);
          this.updateDescription(lastCategory);
        }
      } else {
        // No active triggers and not near bottom, clear everything
        this.isScrollTriggered = false;
        this.currentScrollCategory = null;

        // Only clear if mouse is not hovering
        if (!this.isMouseHovering) {
          this.clearHighlight();
          this.resetDescription();
        }
      }
    }
  }

  // Helper method to find the currently active item based on scroll position
  findActiveItemByScrollPosition() {
    const scrollY = window.scrollY;
    const viewportHeight = window.innerHeight;
    const centerY = scrollY + viewportHeight / 2;

    let closestItem = null;
    let closestDistance = Infinity;

    this.titleItems.forEach((item) => {
      const rect = item.getBoundingClientRect();
      const itemCenterY = scrollY + rect.top + rect.height / 2;
      const distance = Math.abs(centerY - itemCenterY);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestItem = item;
      }
    });

    return closestItem;
  }

  setupScrollListener() {
    let scrollTimeout;

    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        // Only update if not hovering and on desktop
        if (!this.isMouseHovering && window.innerWidth >= 1200) {
          const activeItem = this.findActiveItemByScrollPosition();
          if (activeItem) {
            const category = activeItem.dataset.category;
            const isLastItem =
              activeItem === this.titleItems[this.titleItems.length - 1];

            if (category !== this.currentScrollCategory) {
              this.isScrollTriggered = true;
              this.currentScrollCategory = category;
              this.highlightCategory(category);
              this.updateDescription(category);
            }

            // For last item, ensure it stays visible even when scrolling past
            if (isLastItem) {
              const rect = activeItem.getBoundingClientRect();
              const viewportHeight = window.innerHeight;
              const isNearBottom = rect.bottom < viewportHeight * 0.3; // When near bottom of viewport

              if (isNearBottom) {
                // Keep the last item active even when scrolling past it
                this.isScrollTriggered = true;
                this.currentScrollCategory = category;
                this.highlightCategory(category);
                this.updateDescription(category);
              }
            }
          }
        }
      }, 50); // Increased timeout for smoother transitions
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    // Store the handler for cleanup
    this.scrollHandler = handleScroll;
  }

  setupScrollTriggers() {
    // Only setup ScrollTriggers for desktop (1024px and above)
    if (window.innerWidth >= 1024) {
      gsap.registerPlugin(ScrollTrigger);

      // Wait for layout to settle before creating triggers
      setTimeout(() => {
        // Create scroll triggers for each title item
        this.titleItems.forEach((item) => {
          const category = item.dataset.category;

          // Check if this is the last item
          const isLastItem =
            item === this.titleItems[this.titleItems.length - 1];

          ScrollTrigger.create({
            trigger: item,
            start: "top 70%",
            end: isLastItem ? "bottom 10%" : "bottom 30%", // Last item has more generous end point
            markers: false, // Set to true for debugging
            invalidateOnRefresh: true,
            refreshPriority: -1,
            onEnter: () => {
              // Always update scroll state
              this.isScrollTriggered = true;
              this.currentScrollCategory = category;

              // Only highlight if mouse is not hovering
              if (!this.isMouseHovering) {
                this.highlightCategory(category);
                this.updateDescription(category);
              }
            },
            onLeave: () => {
              // For last item, don't clear immediately - keep it visible
              if (isLastItem) {
                // Keep the last item visible even when scrolling past it
                return;
              }

              // Use a longer delay to allow other triggers to activate
              setTimeout(() => {
                this.updateActiveScrollCategory();
              }, 100);
            },
            onEnterBack: () => {
              // Always update scroll state
              this.isScrollTriggered = true;
              this.currentScrollCategory = category;

              // Only highlight if mouse is not hovering
              if (!this.isMouseHovering) {
                this.highlightCategory(category);
                this.updateDescription(category);
              }
            },
            onLeaveBack: () => {
              // For last item, don't clear when leaving back
              if (isLastItem) {
                return;
              }

              // Use a longer delay to allow other triggers to activate
              setTimeout(() => {
                this.updateActiveScrollCategory();
              }, 100);
            },
          });
        });

        // Refresh ScrollTrigger after all triggers are created
        // ScrollTrigger.refresh();

        // Add scroll event listener as backup for better activation
        this.setupScrollListener();
      }, 100);

      // Handle font loading and image loading for desktop
      document.fonts.ready.then(() => {
        // ScrollTrigger.refresh();
      });

      // Additional refresh on window load to catch any late-loading content
      window.addEventListener("load", () => {
        setTimeout(() => {
          // ScrollTrigger.refresh();
        }, 500);
      });
    }

    // Handle window resize and manage ScrollTriggers/mobile state
    let resizeTimeout;

    let previousViewportHeight = window.innerHeight;
    const handleResize = () => {
      const currentViewportHeight = window.innerHeight;
      if (currentViewportHeight !== previousViewportHeight) {
        ScrollTrigger.refresh(false);
        previousViewportHeight = currentViewportHeight;
      } else {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          // Reset state flags
          this.isScrollTriggered = false;
          this.currentScrollCategory = null;
          this.isMouseHovering = false;

          // Remove scroll listener
          if (this.scrollHandler) {
            window.removeEventListener("scroll", this.scrollHandler);
            this.scrollHandler = null;
          }

          // Kill existing ScrollTriggers
          ScrollTrigger.getAll().forEach((trigger) => {
            if (
              trigger.trigger &&
              trigger.trigger.closest(".services-storyboard")
            ) {
              trigger.kill();
            }
          });

          // Clear any active states
          this.clearHighlight();
          this.resetDescription();

          // Reinitialize based on new window size
          this.setupScrollTriggers();
          this.handleMobileDefaultState();
        }, 250);
      }
    };
    window.addEventListener("resize", handleResize);
    this.resizeHandler = handleResize;

    // window.addEventListener("resize", () => {
    //   clearTimeout(resizeTimeout);
    //   resizeTimeout = setTimeout(() => {
    //     // Reset state flags
    //     this.isScrollTriggered = false;
    //     this.currentScrollCategory = null;
    //     this.isMouseHovering = false;

    //     // Remove scroll listener
    //     if (this.scrollHandler) {
    //       window.removeEventListener("scroll", this.scrollHandler);
    //       this.scrollHandler = null;
    //     }

    //     // Kill existing ScrollTriggers
    //     ScrollTrigger.getAll().forEach((trigger) => {
    //       if (
    //         trigger.trigger &&
    //         trigger.trigger.closest(".services-storyboard")
    //       ) {
    //         trigger.kill();
    //       }
    //     });

    //     // Clear any active states
    //     this.clearHighlight();
    //     this.resetDescription();

    //     // Reinitialize based on new window size
    //     this.setupScrollTriggers();
    //     this.handleMobileDefaultState();
    //   }, 250);
    // });
  }
}

// Initialize only if the component exists on the page
if (document.querySelector(".services-storyboard")) {
  new ServicesStoryboard();
}
